/**
 * PageTransition — smooth fade + slide animation when navigating pages.
 *
 * Wraps children in a CSS transition keyed on `locationKey`.
 * Uses only CSS – no external animation libs needed.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const PageTransition = ({ children, locationKey }) => {
  const [displayChildren, setDisplayChildren] = useState(children);
  const [transitionStage, setTransitionStage] = useState('enter');
  const prevKeyRef = useRef(locationKey);

  useEffect(() => {
    if (locationKey !== prevKeyRef.current) {
      setTransitionStage('exit');

      const timeout = setTimeout(() => {
        setDisplayChildren(children);
        setTransitionStage('enter');
        prevKeyRef.current = locationKey;
      }, 150); // exit duration

      return () => clearTimeout(timeout);
    } else {
      setDisplayChildren(children);
    }
  }, [locationKey, children]);

  return (
    <Box
      sx={{
        transition: 'opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
        opacity: transitionStage === 'exit' ? 0 : 1,
        transform: transitionStage === 'exit' ? 'translateY(8px)' : 'translateY(0)',
      }}
    >
      {displayChildren}
    </Box>
  );
};

export default PageTransition;
